import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, MessageSquareText } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const field = "w-full rounded-xl border border-[#E5E0D8] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#144F75]";

export const StudioOverlayPanel = ({ eid, info, refresh }) => {
  const { user } = useAuth();
  const ov = info.overlay || {};
  const live = info.status === "live";
  const [title, setTitle] = useState(ov.banner_title || info.people?.[0]?.name || "");
  const [subtitle, setSubtitle] = useState(ov.banner_subtitle || info.people?.[0]?.subtitle || "");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const people = info.people?.length ? info.people : [user?.name, ...(info.guests || []).map((g) => g.name)].filter(Boolean).map((n) => ({ name: n, subtitle: "" }));
  const send = async (body, done) => {
    setBusy(true);
    try { await api.post(`/events/${eid}/studio/live/overlay`, body); toast.success(done); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update the broadcast"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4 border-t border-[#E5E0D8] pt-4" data-testid="studio-overlay-panel">
      <p className="text-xs text-[#4A5D6B]">On-screen branding {live ? "" : "— available once you're live"}</p>
      <fieldset disabled={!live || busy} className="space-y-2 disabled:opacity-60">
        <legend className="text-xs font-semibold text-[#1A242B] mb-1">Name banner</legend>
        <div className="flex flex-wrap gap-1.5">
          {people.map((p) => (
            <button type="button" key={p.name} onClick={() => { setTitle(p.name); setSubtitle(p.subtitle || ""); }} data-testid={`studio-banner-name-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
              title={p.subtitle || ""} className="text-xs rounded-full border border-[#E5E0D8] px-2.5 py-1 hover:border-[#144F75]">{p.name}</button>
          ))}
        </div>
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={60} placeholder="Name" aria-label="Banner name" data-testid="studio-banner-title" className={field} />
        <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} maxLength={80} placeholder="Role, e.g. Reiki Practitioner" aria-label="Banner role" data-testid="studio-banner-subtitle" className={field} />
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => send({ banner_title: title, banner_subtitle: subtitle, banner_show: true }, "Banner on screen")} data-testid="studio-banner-show" className="eyw-btn !py-2 text-sm justify-center"><Eye size={14} /> Show</button>
          <button type="button" onClick={() => send({ banner_show: false }, "Banner hidden")} data-testid="studio-banner-hide" className="eyw-btn-outline !py-2 text-sm justify-center"><EyeOff size={14} /> Hide</button>
        </div>
      </fieldset>
      <fieldset disabled={!live || busy} className="space-y-2 disabled:opacity-60">
        <legend className="text-xs font-semibold text-[#1A242B] mb-1">Pop-up message</legend>
        <div className="flex gap-2">
          <input value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={120} placeholder="Book at enhancingyourworld.com" aria-label="Pop-up message" data-testid="studio-toast-input" className={field} />
          <button type="button" onClick={() => msg.trim() && send({ toast: msg }, "Message shown for 6 seconds").then(() => setMsg(""))} aria-label="Show message" data-testid="studio-toast-send" className="eyw-btn !px-3 !py-2"><MessageSquareText size={15} /></button>
        </div>
      </fieldset>
    </div>
  );
};

// Approximate preview of what viewers see on top of the cameras.
export const BrandPreview = ({ info }) => {
  const ov = info.overlay || {};
  if (!info.brand) return null;
  return (
    <div className="pointer-events-none absolute inset-0" data-testid="studio-brand-preview">
      <img src={info.brand.logo} alt="Enhancing Your World" className="absolute top-3 right-3 h-6 opacity-95 drop-shadow" />
      {info.status === "live" && ov.banner_show && ov.banner_title && (
        <div className="absolute bottom-16 left-3 rounded-md border px-3 py-1.5 text-[#F9F7F3]" style={{ background: "rgba(20,79,117,0.92)", borderColor: info.brand.terracotta }} data-testid="studio-banner-preview">
          <p className="text-sm font-semibold leading-tight">{ov.banner_title}</p>
          {ov.banner_subtitle && <p className="text-xs opacity-90">{ov.banner_subtitle}</p>}
        </div>
      )}
    </div>
  );
};
